/**
 * `media` host requests (docs/ops-protocol.md, "Video and audio"):
 * AVFoundation's files and metadata probes, and QuickLook's previews.
 *
 * - `file` (`name`, `type`, `data` base64): a blob of the bytes; answers
 *   `{ url }`, an object URL a `video` element can play. With `path` in
 *   place of `data` (a resource of the app's bundle), the URL the page
 *   serves that resource at, so the browser streams it and nothing is
 *   copied; fails `unsupported: …` when the page has no such resource,
 *   and the app sends the bytes instead.
 * - `probe` (`url`): reads the media's metadata in a detached `<video>`;
 *   answers `{ duration, width, height }` (`duration` null for a stream),
 *   fails (`network: …`) when the browser cannot load or decode it.
 * - `release` (`url`): revokes an object URL `file` made.
 * - `open` (`url`, or `name` / `type` / `data`): opens the URL, or the file
 *   as a blob, in a new tab; when the browser blocks the tab (no tap just
 *   before), a file is downloaded instead. Answers `{ opened }`.
 */

import type { MediaRequestParams } from './protocol';

/** Standard base64 to bytes (hostRequests.ts imports this module, so not from there). */
function decode(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export interface MediaInfo {
  duration: number | null;
  width: number;
  height: number;
}

/** What the page does for `media` requests; tests pass their own. */
export interface MediaHost {
  objectURL(blob: Blob): string;
  revoke(url: string): void;
  probe(url: string): Promise<MediaInfo>;
  /** Opens `url` in a new tab; false when the browser blocked it. */
  open(url: string): boolean;
  download?(blob: Blob, filename: string): void;
  /** The URL the page serves the bundle resource at `path` from, or null for a resource it did not load. */
  bundleURL?(path: string): string | null;
}

let bundleLocator: ((path: string) => string | null) | null = null;

/**
 * Tells the media host where the app's bundle resources are served from
 * (src/main.ts, once the bundle is loaded), for `file` requests by `path`.
 */
export function locateBundleFiles(locator: ((path: string) => string | null) | null): void {
  bundleLocator = locator;
}

export async function performMedia(params: MediaRequestParams, host: MediaHost | undefined): Promise<Record<string, unknown>> {
  if (!host) throw new Error('unsupported: this page has no media host');
  switch (params.action) {
    case 'file': {
      if (params.path !== undefined) {
        const url = host.bundleURL?.(params.path) ?? null;
        if (url === null) throw new Error(`unsupported: the page has no bundle resource ${params.path}`);
        return { url };
      }
      const blob = new Blob([decode(params.data ?? '') as BlobPart], { type: params.type ?? '' });
      return { url: host.objectURL(blob) };
    }
    case 'probe': {
      if (!params.url) throw new Error('malformed request: probe needs a url');
      const info = await host.probe(params.url);
      return { ...info };
    }
    case 'release':
      if (params.url?.startsWith('blob:')) host.revoke(params.url);
      return {};
    case 'open': {
      if (params.url !== undefined) {
        if (!/^(https?|blob|data):/i.test(params.url)) throw new Error(`unsupported: cannot open ${params.url}`);
        return { opened: host.open(params.url) };
      }
      const blob = new Blob([decode(params.data ?? '') as BlobPart], { type: params.type ?? '' });
      const url = host.objectURL(blob);
      if (host.open(url)) {
        // The tab has loaded it long before then.
        setTimeout(() => host.revoke(url), 60_000);
        return { opened: true };
      }
      host.revoke(url);
      host.download?.(blob, params.name || 'Preview');
      return { opened: false };
    }
    default:
      throw new Error(`unsupported: media action ${String((params as { action: unknown }).action)}`);
  }
}

/** Reads metadata in a detached `<video>` (no sound, no frames beyond the first). */
function probe(url: string): Promise<MediaInfo> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const finish = (): void => {
      video.removeAttribute('src');
      video.load();
    };
    video.addEventListener(
      'loadedmetadata',
      () => {
        const info = {
          duration: Number.isFinite(video.duration) ? video.duration : null,
          width: video.videoWidth,
          height: video.videoHeight,
        };
        finish();
        resolve(info);
      },
      { once: true },
    );
    video.addEventListener(
      'error',
      () => {
        const message = video.error?.message || `could not load ${url}`;
        finish();
        reject(new Error(`network: ${message}`));
      },
      { once: true },
    );
    video.src = url;
  });
}

export function browserMediaHost(download?: (blob: Blob, filename: string) => void): MediaHost | undefined {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
  return {
    objectURL: (blob) => URL.createObjectURL(blob),
    revoke: (url) => URL.revokeObjectURL(url),
    probe,
    open: (url) => {
      const tab = window.open(url, '_blank');
      if (!tab) return false;
      try {
        tab.opener = null;
      } catch {
        // Another origin's window: nothing to cut.
      }
      return true;
    },
    ...(download ? { download } : {}),
    bundleURL: (path) => bundleLocator?.(path) ?? null,
  };
}
